/**
 * STELLYFIRE — Complete Vanilla JS
 * Cart AJAX, cursor, scroll, carousel, reveals, tabs, mobile menu
 */
(function () {
  'use strict';

  /* ─── Utilities ─────────────────────────────────────── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ─── Custom cursor (pointer-fine only) ─────────────── */
  function initCursor() {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    const cursor = qs('#sf-cursor');
    if (!cursor) return;
    let cx = -99, cy = -99;
    document.addEventListener('mousemove', function (e) {
      cx = e.clientX;
      cy = e.clientY;
      cursor.style.transform = 'translate(' + (cx - cursor.offsetWidth / 2) + 'px,' + (cy - cursor.offsetHeight / 2) + 'px)';
    });
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest('a,button,[data-hover]')) {
        cursor.classList.add('hover');
      } else {
        cursor.classList.remove('hover');
      }
    });
  }

  /* ─── Spatula scroll indicator ──────────────────────── */
  function initScroll() {
    const wrap = qs('#sf-scroll');
    if (!wrap) return;
    const thumb = qs('.sf-scroll-thumb', wrap);
    if (!thumb) return;
    function update() {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const prog = h > 0 ? window.scrollY / h : 0;
      thumb.style.top = 'calc(' + (prog * 100) + '% - ' + (prog * 32) + 'px)';
    }
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ─── Header scroll behavior ────────────────────────── */
  function initHeader() {
    const header = qs('.sf-header');
    const announce = qs('.sf-announce');
    if (!header) return;
    function update() {
      if (window.scrollY > 20) {
        header.classList.add('scrolled');
        if (announce) announce.classList.add('hidden');
      } else {
        header.classList.remove('scrolled');
        if (announce) announce.classList.remove('hidden');
      }
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ─── Mobile menu ───────────────────────────────────── */
  function initMobileMenu() {
    const openBtn  = qs('#sf-menu-open');
    const closeBtn = qs('#sf-menu-close');
    const menu     = qs('#sf-mnav');
    if (!menu) return;
    if (openBtn) openBtn.addEventListener('click', function () { menu.classList.add('open'); });
    if (closeBtn) closeBtn.addEventListener('click', function () { menu.classList.remove('open'); });
    // close on nav link click
    qsa('.sf-mnav .nav-link', menu).forEach(function (a) {
      a.addEventListener('click', function () { menu.classList.remove('open'); });
    });
  }

  /* ─── Reveal on scroll ──────────────────────────────── */
  function initReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      qsa('.reveal').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    qsa('.reveal').forEach(function (el) { io.observe(el); });
  }

  /* ─── Product Showcase tabs ─────────────────────────── */
  function initShowcaseTabs() {
    const tabs    = qsa('.sf-tab-btn');
    const panels  = qsa('.sf-showcase-panel');
    if (!tabs.length || !panels.length) return;
    tabs.forEach(function (btn, idx) {
      btn.addEventListener('click', function () {
        tabs.forEach(function (t) { t.classList.remove('active'); });
        panels.forEach(function (p) {
          p.style.display = 'none';
          p.classList.remove('sf-fup');
        });
        btn.classList.add('active');
        if (panels[idx]) {
          panels[idx].style.display = '';
          // force reflow
          void panels[idx].offsetWidth;
          panels[idx].classList.add('sf-fup');
        }
      });
    });
    // init first
    if (tabs[0]) tabs[0].click();
  }

  /* ─── Product grid carousel ─────────────────────────── */
  function initProductCarousel() {
    const track = qs('#sf-gtrack');
    const prevBtn = qs('#sf-garr-prev');
    const nextBtn = qs('#sf-garr-next');
    if (!track) return;
    function slide(dir) {
      var card = qs('.sf-prod', track);
      var cardW = card ? card.offsetWidth + 20 : 320;
      track.scrollBy({ left: dir * cardW, behavior: 'smooth' });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { slide(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { slide(1); });
  }

  /* ─── Reviews carousel ──────────────────────────────── */
  function initReviewsCarousel() {
    const track   = qs('#sf-revt');
    const prevBtn = qs('#sf-rarr-prev');
    const nextBtn = qs('#sf-rarr-next');
    if (!track) return;
    function slide(dir) {
      track.scrollBy({ left: dir * 300, behavior: 'smooth' });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { slide(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { slide(1); });
  }

  /* ─── Cart AJAX API ─────────────────────────────────── */
  var Cart = {
    /**
     * Add a variant to the cart.
     * @param {number|string} variantId
     * @param {number} quantity
     */
    add: function (variantId, quantity) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
      }).then(function (r) { return r.json(); });
    },

    /**
     * Get current cart state.
     */
    get: function () {
      return fetch('/cart.js').then(function (r) { return r.json(); });
    },

    /**
     * Update an item quantity by its line-item key.
     * @param {string} key
     * @param {number} quantity  — 0 removes it
     */
    update: function (key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) { return r.json(); });
    }
  };

  /* ─── Cart drawer ───────────────────────────────────── */
  function openCartDrawer() {
    var drawer  = qs('#sf-drw');
    var overlay = qs('#sf-scm');
    if (drawer)  drawer.classList.add('open');
    if (overlay) overlay.classList.add('open');
    // refresh contents
    refreshCartDrawer();
  }

  function closeCartDrawer() {
    var drawer  = qs('#sf-drw');
    var overlay = qs('#sf-scm');
    if (drawer)  drawer.classList.remove('open');
    if (overlay) overlay.classList.remove('open');
  }

  function refreshCartDrawer() {
    Cart.get().then(function (cart) {
      renderCartDrawer(cart);
      updateCartBadge(cart.item_count);
    }).catch(function () {});
  }

  function renderCartDrawer(cart) {
    var body = qs('#sf-drw-body');
    if (!body) return;

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = '<p class="sf-drw-empty">Your cart is empty.</p>';
      updateDrawerTotal(0);
      return;
    }

    var html = '';
    cart.items.forEach(function (item) {
      var img = item.featured_image && item.featured_image.url
        ? '<img src="' + item.featured_image.url + '" alt="' + escHtml(item.title) + '" style="width:100%;height:100%;object-fit:cover;">'
        : '';
      html += '<div class="sf-drw-item" data-key="' + escHtml(item.key) + '">'
        + '<div class="sf-drw-img">' + img + '</div>'
        + '<div style="flex:1">'
        + '<div class="sf-drw-item-name">' + escHtml(item.product_title) + '</div>'
        + (item.variant_title && item.variant_title !== 'Default Title'
            ? '<div class="sf-drw-item-meta">' + escHtml(item.variant_title) + '</div>'
            : '')
        + '<div class="sf-drw-item-meta">Qty ' + item.quantity + '</div>'
        + '<div class="sf-drw-item-price">' + formatMoney(item.line_price) + '</div>'
        + '</div>'
        + '<button class="sf-ib sf-drw-remove" data-key="' + escHtml(item.key) + '" aria-label="Remove item" style="align-self:flex-start;padding:4px;opacity:0.5;">'
        + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
        + '</button>'
        + '</div>';
    });
    body.innerHTML = html;
    updateDrawerTotal(cart.total_price);

    // bind remove buttons
    qsa('.sf-drw-remove', body).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-key');
        Cart.update(key, 0).then(function (cart) {
          renderCartDrawer(cart);
          updateCartBadge(cart.item_count);
        });
      });
    });
  }

  function updateDrawerTotal(cents) {
    var el = qs('#sf-drw-total');
    if (el) el.textContent = formatMoney(cents);
  }

  function updateCartBadge(count) {
    var badge = qs('#sf-cart-badge');
    if (!badge) return;
    if (count > 0) {
      badge.textContent = count;
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    return '₹' + (cents / 100).toFixed(0);
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ─── Toast notification ────────────────────────────── */
  var toastTimer = null;
  function showToast(msg) {
    var el = qs('#sf-tst');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('show');
    }, 2400);
  }

  /* ─── Add-to-cart buttons ───────────────────────────── */
  function initAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      var variantId = btn.getAttribute('data-variant-id');
      var qty = parseInt(btn.getAttribute('data-quantity') || '1', 10);
      var productTitle = btn.getAttribute('data-product-title') || 'Item';
      if (!variantId) return;
      btn.disabled = true;
      var origText = btn.textContent;
      btn.textContent = 'Adding…';
      Cart.add(variantId, qty)
        .then(function () {
          showToast('Added · ' + productTitle);
          refreshCartDrawer();
          btn.disabled = false;
          btn.textContent = origText;
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = origText;
          showToast('Could not add to cart.');
        });
    });
  }

  /* ─── Cart open / close wiring ──────────────────────── */
  function initCartWiring() {
    var cartBtn = qs('#sf-cart-btn');
    var closeBtn = qs('#sf-drw-close');
    var overlay = qs('#sf-scm');
    if (cartBtn) cartBtn.addEventListener('click', openCartDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeCartDrawer);
    if (overlay) overlay.addEventListener('click', closeCartDrawer);
  }

  /* ─── Initial cart badge load ───────────────────────── */
  function initCartBadge() {
    Cart.get().then(function (cart) {
      updateCartBadge(cart.item_count);
    }).catch(function () {});
  }

  /* ─── Bootstrap ─────────────────────────────────────── */
  function init() {
    initCursor();
    initScroll();
    initHeader();
    initMobileMenu();
    initReveal();
    initShowcaseTabs();
    initProductCarousel();
    initReviewsCarousel();
    initAddToCart();
    initCartWiring();
    initCartBadge();
    initThumbnailClick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* ---- Product thumbnail click (mobile) ---- */
  function initThumbnailClick() {
    if (!document.body.classList.contains('template-product')) return;
    var sfSplit = qs('.sf-split');
    if (!sfSplit) return;
    var imgContainer = sfSplit.children[0];
    if (!imgContainer) return;
    var mainImgWrap = imgContainer.children[0];
    var thumbStrip = imgContainer.children[1];
    if (!mainImgWrap || !thumbStrip) return;
    var thumbDivs = thumbStrip.children;
    Array.from(thumbDivs).forEach(function(thumb, i) {
      thumb.style.cursor = 'pointer';
      thumb.style.opacity = i === 0 ? '1' : '0.5';
      thumb.addEventListener('click', function() {
        var mainImg = mainImgWrap.querySelector('img');
        var thumbImg = thumb.querySelector('img');
        if (!mainImg || !thumbImg) return;
        var newSrc = thumbImg.src
          .replace(/_(60x60|70x70|small|thumb)[^.]*/, '')
          .replace(/\?.*$/, '');
        mainImg.src = newSrc;
        mainImg.srcset = '';
        Array.from(thumbDivs).forEach(function(t) { t.style.opacity = '0.5'; });
        thumb.style.opacity = '1';
      });
    });
  }
})();
